/**
 * Test run: the tool on screen, saved or not, run with sample details.
 *
 * THE SAME CODE A CALL USES, through `POST /v1/agent/tools/test`, so what comes back is what a
 * caller would get - and it never sends the tool's notification email. Three blocks: what was
 * SENT, what CAME BACK, and what THE AI HEARS, the last one tinted because it is the one that
 * matters.
 *
 * A SECTION OF THE EDITOR, NOT A PANEL BESIDE IT (2026-09-29). The sample details and the last
 * result belong to the editor, which keeps them while somebody goes to Request to fix what
 * the run showed and comes back.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { ApiError, post, type ToolTestResult } from "../../../api";
import { Button, StatusPill, TextInput, cx } from "../../../ui";
import { Picker } from "../../Listbox";
import { fromEditor, typedArgs, type EditorTool } from "./toolModel";

export type TestRun = { args: Record<string, string>; result: ToolTestResult | null };

export function TestRunPanel({
  agentId,
  toolId,
  tool,
  run: state,
  onRun,
  onAnswer,
  onErrorLine,
}: {
  agentId: string;
  toolId: string | null;
  tool: EditorTool;
  /** The sample details and the last result, kept by the editor across sections. */
  run: TestRun;
  onRun: (next: (current: TestRun) => TestRun) => void;
  /** The reply's body, so the mapping can offer its fields. */
  onAnswer: (body: string) => void;
  /** The line of a code tool that the run's error points at, or null, so the editor can mark it. */
  onErrorLine: (line: number | null) => void;
}) {
  const { args, result } = state;
  const setArgs = (next: (current: Record<string, string>) => Record<string, string>) => onRun((current) => ({ ...current, args: next(current.args) }));
  const setResult = (next: ToolTestResult | null) => onRun((current) => ({ ...current, result: next }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const answer = await post<ToolTestResult>("/v1/agent/tools/test", {
        agent: agentId,
        toolId,
        tool: fromEditor(tool),
        args: typedArgs(tool.params, args),
      });
      setResult(answer);
      if (answer.response?.body) onAnswer(answer.response.body);
      onErrorLine(answer.errorLine ?? null);
    } catch (err) {
      setResult(null);
      onErrorLine(null);
      setError(err instanceof ApiError ? err.message : "The dashboard could not reach the server.");
    } finally {
      setBusy(false);
    }
  }

  const sent = typedArgs(tool.params, args);

  return (
    <div className="grid gap-6">
      <div className="grid max-w-[520px] gap-3">
        {tool.params.length === 0 && <p className="text-sm text-ink">No parameters to fill in.</p>}
        {tool.params.map((param) => {
          const name = `${param.name || "(unnamed)"}${param.required ? " *" : ""}`;
          const choices = param.type === "boolean" ? ["true", "false"] : param.valuesOn ? param.values : [];
          return (
            // A div, not a label, around a picker: a click on one of its rows would reach the
            // label and press the picker's button again, reopening it.
            <div key={param.name} className="grid gap-1">
              <span className="font-mono text-sm text-ink">{name}</span>
              {choices.length ? (
                <Picker
                  label={name}
                  value={args[param.name] ?? ""}
                  options={[{ value: "", label: "Not given" }, ...choices.map((value) => ({ value, label: value }))]}
                  onChange={(value) => setArgs((current) => ({ ...current, [param.name]: value }))}
                />
              ) : (
                <TextInput
                  aria-label={name}
                  type={param.type === "number" || param.type === "integer" ? "number" : "text"}
                  className="w-full"
                  placeholder={param.type === "array" ? "comma, separated" : param.format ?? param.type}
                  value={args[param.name] ?? ""}
                  onChange={(event) => setArgs((current) => ({ ...current, [param.name]: event.target.value }))}
                />
              )}
            </div>
          );
        })}
        <Button type="button" variant="primary" className="mt-1 justify-self-start" disabled={busy} onClick={() => void run()}>
          {busy ? "Running…" : result ? "Run again" : "Run test"}
        </Button>
        {error && <p className="text-sm text-alert">{error}</p>}
      </div>

      {result && !busy && (
        <div className="grid gap-1 border-t border-line-soft pt-5">
          <div className="flex items-center gap-2">
            <StatusPill tone={result.status === "ok" ? "good" : "alert"}>
              {result.status === "ok"
                ? "Worked"
                : result.status === "rejected"
                  ? "Details refused"
                  : `Failed · ${result.errorKind ?? "error"}`}
            </StatusPill>
            <span className="nums text-sm text-ink">
              {result.durationMs} ms{tool.kind === "http" && result.attempts > 1 ? ` · ${result.attempts} attempts` : ""}
            </span>
          </div>
          {tool.kind === "save" && result.status === "ok" && (
            // Only when it ran: a refused run added nothing, and the block would say it did.
            <Block label="Added to the call record">{JSON.stringify(sent, null, 2)}</Block>
          )}
          {tool.kind === "code" && result.logs && (
            <Block label="Output" meta={result.errorLine ? `line ${result.errorLine}` : undefined}>
              {result.logs}
            </Block>
          )}
          {tool.kind !== "save" && (
            <>
              {tool.kind === "code" && result.request && (
                <p className="pt-3 text-sm text-ink">
                  {result.attempts === 1 ? "1 request made." : `${result.attempts} requests made; the last one:`}
                </p>
              )}
              {result.request && (
                <Block label="Sent" meta={result.request.method}>
                  {`${result.request.method} ${result.request.url}`}
                  {result.request.body ? `\n\n${result.request.body}` : ""}
                </Block>
              )}
              {result.response && (
                <Block
                  label="Came back"
                  meta={`${result.response.status} · ${result.durationMs} ms${result.response.truncated ? " · cut short" : ""}`}
                  good={result.response.status < 400}
                >
                  {result.response.body || "(empty)"}
                </Block>
              )}
            </>
          )}
          <Block label="The AI hears" last>
            {result.modelReceives}
          </Block>
        </div>
      )}
    </div>
  );
}

function Block({
  label,
  meta,
  good,
  last,
  children,
}: {
  label: string;
  meta?: string;
  good?: boolean;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1.5 py-3">
      <span className="flex items-center justify-between text-sm font-medium text-ink">
        {label}
        {meta && <span className={cx("font-mono font-normal", good ? "text-good" : "text-ink")}>{meta}</span>}
      </span>
      <pre
        className={cx(
          "max-h-80 overflow-auto rounded-sm px-3 py-2.5 font-mono text-sm leading-relaxed whitespace-pre-wrap text-ink [overflow-wrap:anywhere]",
          last ? "bg-accent-soft" : "bg-sunken",
        )}
      >
        {children}
      </pre>
    </div>
  );
}
